import React from 'react';
import { Box, Button, Grid, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useTranslation } from 'next-i18next';
import { formatPriceWithNoUnit } from '@/utils/formatPrice';
import { CardFooter } from '@/containers/TourBookingByCustomer/TourBookingForm/style';
import { IPromotion } from '@/types/interface';

interface ComputedPricing {
  pureTourPrice?: number;
  additional_price?: number;
  visitPlaceTicketFee?: number;
  [key: string]: any;
}

interface SummarizationTourInfoProps {
  promotion?: IPromotion | null;
  computedPricing?: ComputedPricing | null;
  isDisableSubmit?: boolean;
  onSubmitClick?: () => void;
  sxContainer?: any;
  sxLeft?: any;
  sxRight?: any;
  toolTipPosition?: 'behindTotal' | 'inFrontOfSubmit';
  summaryType?: 'customer' | "partner";
}

const SummarizationTourInfo: React.FC<SummarizationTourInfoProps> = ({
  promotion,
  computedPricing,
  isDisableSubmit = false,
  onSubmitClick,
  sxContainer,
  sxLeft,
  sxRight,
  toolTipPosition = 'inFrontOfSubmit',
  summaryType = 'partner'
}) => {
  const { t } = useTranslation();
  const isCustomer = summaryType === 'customer';
  return (
    <CardFooter
      container
      columnSpacing={{ xs: 0, md: 3.5 }}
      rowSpacing={{ xs: 2, md: 0 }}
      sx={{ ...sxContainer }}
    >
      <Grid item xs={isCustomer ? 7 : 12} md={8}>
        <Box
          display="flex"
          gap={1}
          sx={{
            flexDirection: {
              xs: isCustomer ? 'column' : 'row',
              md: 'column',
            },
            justifyContent: {
              xs: isCustomer ? 'start' : 'space-between',
              md: 'center',
            },
            alignItems: {
              xs: 'center',
              md: 'flex-end',
            },
            ...sxLeft,
          }}
        >
          <Box display='flex' flexDirection='row' gap={1} alignItems={'end'}>
            {
              summaryType === 'customer' ? (
                <CustomerTotalInfo computedPricing={computedPricing} />
              ) : (
                !!promotion ? <SumarizationTourDiscountInfo computedPricing={computedPricing} />
                  :
                  <SumarizationTourTotalInfo computedPricing={computedPricing} />
              )
            }
            {toolTipPosition === 'behindTotal' && <SummarizationToolTip computedPricing={computedPricing} summaryType={summaryType} />}
          </Box>
        </Box>
      </Grid>
      <Grid item xs={isCustomer ? 5 : 12} md={4}>

        <Box display="flex" justifyContent="flex-end" gap={2} alignItems='end' sx={{ ...sxRight }}>
          {
            toolTipPosition === 'inFrontOfSubmit' &&
            <SummarizationToolTip
              computedPricing={computedPricing}
              summaryType={summaryType}
            />
          }
          <Button
            variant="contained"
            type="submit"
            fullWidth
            disabled={isDisableSubmit}
            onClick={onSubmitClick}
            sx={{
              px: {
                xs: 2,
                md: 'auto'
              }
            }}
          >
            <Typography
              fontWeight={600}
              color={theme => theme.palette.white.main}
            >
              {t('common:bookTourNow').toUpperCase()}
            </Typography>
          </Button>
        </Box>
      </Grid>
    </CardFooter>
  );
};

const SumarizationTourTotalInfo = ({ computedPricing }: { computedPricing?: ComputedPricing | null }) => {
  const { t } = useTranslation();

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Typography variant="body4">
        {t('common:totalAmount')}
      </Typography>
      <Typography variant="body3" fontWeight={600}>
        {formatPriceWithNoUnit(computedPricing?.money)} VNĐ
      </Typography>
    </Box>
  );
};

const SumarizationTourDiscountInfo = ({ computedPricing }: { computedPricing?: ComputedPricing | null }) => {
  const { t } = useTranslation();

  return (
    <Box display="flex" gap={1}>
      {/* Discount */}
      <Box display="flex" flexDirection="column" justifyContent="space-between" alignItems='flex-end' gap={1}>

        {/* Tour price */}
        <Box display="flex" gap={1}>
          <Typography
            variant="body4"
          >
            {t('common:tourPrice')}
          </Typography>
          <Typography
            variant="body4"
            fontWeight={600}
          >
            {formatPriceWithNoUnit(computedPricing?.price)} VNĐ
          </Typography>
        </Box>

        {/* Promotion price */}
        <Box display="flex" gap={1}>
          <Typography
            variant="body4"
          >
            {t('common:discount')}
          </Typography>
          <Typography
            variant="body4"
            fontWeight={600}
          >
            -{formatPriceWithNoUnit(computedPricing?.discount)} VNĐ
          </Typography>
        </Box>

        {/* Tour price after discount */}
        <Box display="flex" gap={1}>

          <Typography variant="body4">
            {t('common:totalAmount')}
          </Typography>
          {/* Total Amount */}
          <Typography
            variant="body3"
            fontWeight={600}

          >
            {formatPriceWithNoUnit(computedPricing?.money)} VNĐ
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Customer Total Info - shows total with primary color
const CustomerTotalInfo = ({ computedPricing }: { computedPricing?: ComputedPricing | null }) => {
  const { t } = useTranslation();

  return (
    <Box display="flex" flexDirection="column" gap={0.75}>
      <Typography variant="body1" color="text.primary" fontWeight={500}>
        {t('common:totalAmount')}
      </Typography>
      <Typography variant="h2" fontWeight={700} color="primary" fontSize={24}>
        {formatPriceWithNoUnit(computedPricing?.money)} vnđ
      </Typography>
    </Box>
  );
};

interface ISummarizationToolTipProps {
  computedPricing?: ComputedPricing | null;
  summaryType?: 'customer' | 'partner';
}

const SummarizationToolTip = ({ computedPricing, summaryType = 'partner' }: ISummarizationToolTipProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);

  const isCustomer = summaryType === 'customer';
  const hasDiscount = isCustomer && computedPricing?.discount && computedPricing.discount > 0;

  const fontSize = isCustomer ? 16 : 12;

  return (
    <Tooltip
      title={
        (
          <Box display="flex" flexDirection="column" gap={2} sx={{ p: 1 }}>
            {isCustomer && (
              <Typography variant="h2" fontWeight={600} sx={{ mb: 1 }}>
                {t('common:paymentDetails')}
              </Typography>
            )}
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
              <Typography variant="body1" sx={{ fontSize }}>{t('common:tourPriceOriginal')}</Typography>
              <Typography variant="body1" sx={{ fontSize, fontWeight: 600 }}>{formatPriceWithNoUnit(computedPricing?.pureTourPrice)} vnđ</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
              <Typography variant="body1" sx={{ fontSize }}>{t('common:additionalService')}</Typography>
              <Typography variant="body1" sx={{ fontSize, fontWeight: 600 }}>{formatPriceWithNoUnit(computedPricing?.additional_price)} vnđ</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
              <Typography variant="body1" sx={{ fontSize }}>{t('common:visitPlaceTicketFees')}</Typography>
              <Typography variant="body1" sx={{ fontSize, fontWeight: 600 }}>{computedPricing && computedPricing?.visitPlaceTicketFee ? formatPriceWithNoUnit(computedPricing?.visitPlaceTicketFee) : '--'} vnđ</Typography>
            </Box>
            {hasDiscount ? (
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                <Typography variant="body1" sx={{ fontSize }}>{t('common:discount')}</Typography>
                <Typography variant="body1" sx={{ fontSize, fontWeight: 600 }}>-{formatPriceWithNoUnit(computedPricing?.discount)} VNĐ</Typography>
              </Box>
            ) : null}
          </Box>
        )
      }
      placement="top"
      arrow
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      slotProps={{
        tooltip: {
          sx: isCustomer ? {
            backgroundColor: '#ffffff !important',
            color: '#000000',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
            borderRadius: 2,
            p: 2,
            minWidth: 400,
            ml: {
              xs: '1.5rem',
              md: 0
            }
          } : {},
        },
        arrow: {
          sx: isCustomer ? {
            color: '#ffffff !important',
          } : {},
        },
      }}
    >
      <Box
        sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? (
          <KeyboardArrowDownIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />
        ) : (
          <KeyboardArrowUpIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />
        )}
      </Box>
    </Tooltip>
  );
};

export default SummarizationTourInfo;
